import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  LinearProgress,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import { PRESETS, configFromPreset } from "../services/scenarioLoader";

const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Scenario configuration dialog: preset or custom location + date range.
const ScenarioSetup = ({ open, onClose, onLoad, loading, loadStatus, loadError }) => {
  const defaultPreset = PRESETS[0];
  const [presetId, setPresetId] = useState(defaultPreset.id);
  const [lat, setLat] = useState(defaultPreset.center.lat);
  const [lon, setLon] = useState(defaultPreset.center.lon);
  const [radiusKm, setRadiusKm] = useState(defaultPreset.radiusKm);
  const [start, setStart] = useState(toLocalInput(new Date(defaultPreset.startISO)));
  const [end, setEnd] = useState(toLocalInput(new Date(defaultPreset.endISO)));

  const isCustom = presetId === "custom";

  const handlePresetChange = (id) => {
    setPresetId(id);
    const preset = PRESETS.find((p) => p.id === id);
    if (preset) {
      setLat(preset.center.lat);
      setLon(preset.center.lon);
      setRadiusKm(preset.radiusKm);
      setStart(toLocalInput(new Date(preset.startISO)));
      setEnd(toLocalInput(new Date(preset.endISO)));
    }
  };

  const handleLoad = () => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (!(startDate < endDate)) return;
    const preset = PRESETS.find((p) => p.id === presetId);
    if (preset && !isCustom) {
      const cfg = configFromPreset(preset);
      cfg.startDate = startDate;
      cfg.endDate = endDate;
      cfg.center = { lat: Number(lat), lon: Number(lon) };
      cfg.radiusKm = Number(radiusKm);
      onLoad(cfg);
    } else {
      onLoad({
        name: `Custom scenario (${Number(lat).toFixed(3)}, ${Number(lon).toFixed(3)})`,
        center: { lat: Number(lat), lon: Number(lon) },
        radiusKm: Number(radiusKm),
        startDate,
        endDate,
        ignitions: [{ lat: Number(lat), lon: Number(lon) }],
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PublicIcon color="info" fontSize="small" />
        Scenario Setup
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Scenario preset"
              value={presetId}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom location…</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Latitude" type="number"
              value={lat} onChange={(e) => setLat(e.target.value)} inputProps={{ step: 0.01 }} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Longitude" type="number"
              value={lon} onChange={(e) => setLon(e.target.value)} inputProps={{ step: 0.01 }} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth size="small" label="Radius (km)" type="number"
              value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)}
              inputProps={{ min: 2, max: 25, step: 1 }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Start (ignition time)" type="datetime-local"
              value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="End" type="datetime-local"
              value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Terrain, water, and building footprints are fetched live for any
              location (AWS Terrain Tiles + OpenStreetMap); hourly weather
              comes from the Open-Meteo archive (available up to ~5 days ago).
              {isCustom && " Custom scenarios ignite at the center point — use the Backfire tool for more ignitions."}
            </Typography>
          </Grid>
          {loading && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                {loadStatus || "Loading…"}
              </Typography>
            </Grid>
          )}
          {loadError && (
            <Grid item xs={12}>
              <Alert severity="error">{loadError}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleLoad} disabled={loading}>
          Load Scenario
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioSetup;
